import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Wrench,
  Clock,
  User,
  DollarSign,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Play,
  Pause,
  X,
  Eye,
  Edit,
  Trash2,
  Filter,
  RefreshCw,
  Droplet,
  Zap,
  Paintbrush,
  Home,
  Settings,
  Bug,
  Shield,
  HardHat
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MaintenanceRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [roomFilter, setRoomFilter] = useState('all');
  
  // Update form data
  const [updateData, setUpdateData] = useState({
    status: '',
    assigned_to: '',
    completion_notes: '',
    actual_cost: ''
  });

  useEffect(() => {
    fetchRequests();
    fetchRooms();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/maintenance-requests`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching maintenance requests:', error);
      toast.error('Failed to load maintenance requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/rooms`);
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const getMaintenanceTypeIcon = (type) => {
    const iconMap = {
      plumbing: Droplet,
      electrical: Zap,
      hvac: Settings,
      cleaning: Paintbrush,
      painting: Paintbrush,
      flooring: Home,
      windows_doors: Home,
      appliances: Settings,
      furniture: Home,
      safety_security: Shield,
      pest_control: Bug,
      general_repairs: Wrench,
      deep_cleaning: Paintbrush,
      inspection: Eye,
      other: HardHat
    };
    return iconMap[type] || Wrench;
  };

  const getPriorityColor = (priority) => {
    const colorMap = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800',
      emergency: 'bg-red-600 text-white'
    };
    return colorMap[priority] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colorMap = {
      requested: 'bg-blue-100 text-blue-800',
      assigned: 'bg-purple-100 text-purple-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      requested: Clock,
      assigned: User,
      in_progress: Play,
      completed: CheckCircle,
      cancelled: X
    };
    return iconMap[status] || Clock;
  };

  const handleUpdateRequest = async (requestId) => {
    try {
      setUpdateLoading(true);
      
      // Filter out empty fields and handle cost properly
      const payload = {};
      if (updateData.status) payload.status = updateData.status;
      if (updateData.assigned_to) payload.assigned_to = updateData.assigned_to;
      if (updateData.completion_notes) payload.completion_notes = updateData.completion_notes;
      
      // Only include actual_cost if it has a valid numeric value
      if (updateData.actual_cost && !isNaN(parseFloat(updateData.actual_cost))) {
        payload.actual_cost = parseFloat(updateData.actual_cost);
      }
      
      const response = await axios.put(`${API}/maintenance-requests/${requestId}`, payload);
      toast.success(`✅ Maintenance request updated successfully! Status: ${updateData.status}, Assigned to: ${updateData.assigned_to || 'Unassigned'}`);
      setShowUpdateDialog(false);
      setSelectedRequest(null);
      setUpdateData({ status: '', assigned_to: '', completion_notes: '', actual_cost: '' });
      fetchRequests(); // Refresh the list to show updated data
    } catch (error) {
      console.error('Error updating maintenance request:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update maintenance request';
      toast.error(`❌ Update failed: ${errorMessage}`);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteRequest = (request) => {
    if (!user || user.role !== 'admin') {
      toast.error('Only administrators can delete maintenance requests');
      return;
    }
    
    setRequestToDelete(request);
    setShowDeleteDialog(true);
  };

  const confirmDeleteRequest = async () => {
    if (!requestToDelete) return;

    try {
      await axios.delete(`${API}/maintenance-requests/${requestToDelete.id}`);
      toast.success('Maintenance request deleted successfully');
      setShowDeleteDialog(false);
      setRequestToDelete(null);
      fetchRequests();
    } catch (error) {
      console.error('Error deleting maintenance request:', error);
      toast.error('Failed to delete maintenance request');
      setShowDeleteDialog(false);
      setRequestToDelete(null);
    }
  };

  const cancelDeleteRequest = () => {
    setShowDeleteDialog(false);
    setRequestToDelete(null);
  };

  // Filter requests
  const filteredRequests = requests.filter(request => {
    if (statusFilter !== 'all' && request.status !== statusFilter) return false;
    if (priorityFilter !== 'all' && request.priority !== priorityFilter) return false;
    if (typeFilter !== 'all' && request.maintenance_type !== typeFilter) return false;
    if (roomFilter !== 'all' && request.room_id !== roomFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Maintenance Requests</h1>
            <p className="text-gray-600">Manage facility maintenance and repairs</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="card-shadow">
              <CardContent className="p-6">
                <div className="skeleton h-32 w-full rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <Wrench className="h-8 w-8 text-orange-600" />
            <span>Maintenance Requests</span>
          </h1>
          <p className="text-gray-600 mt-1">Track and manage facility maintenance tasks</p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <Button 
            onClick={fetchRequests}
            variant="outline"
            disabled={loading}
            className="border-orange-600 text-orange-600 hover:bg-orange-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {['requested', 'assigned', 'in_progress', 'completed', 'cancelled'].map(status => {
          const count = requests.filter(r => r.status === status).length;
          const StatusIcon = getStatusIcon(status);
          return (
            <Card key={status} className="card-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 capitalize">
                      {status.replace('_', ' ')}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">{count}</p>
                  </div>
                  <StatusIcon className="h-6 w-6 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>Filter Requests</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="requested">Requested</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Priority</label>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger>
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Type</label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="plumbing">Plumbing</SelectItem>
                  <SelectItem value="electrical">Electrical</SelectItem>
                  <SelectItem value="hvac">HVAC</SelectItem>
                  <SelectItem value="cleaning">Cleaning</SelectItem>
                  <SelectItem value="general_repairs">General Repairs</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Room</label>
              <Select value={roomFilter} onValueChange={setRoomFilter}>
                <SelectTrigger>
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Rooms</SelectItem>
                  {rooms.map(room => (
                    <SelectItem key={room.id} value={room.id}>
                      Room {room.room_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      {filteredRequests.length === 0 ? (
        <Card className="card-shadow">
          <CardContent className="p-12 text-center">
            <Wrench className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {requests.length === 0 ? 'No maintenance requests yet' : 'No requests match your filters'}
            </h3>
            <p className="text-gray-600 mb-4">
              {requests.length === 0 
                ? 'Maintenance requests will appear here when created from Room Management.' 
                : 'Try adjusting your filters to see more requests.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredRequests.map((request) => {
            const TypeIcon = getMaintenanceTypeIcon(request.maintenance_type);
            const StatusIcon = getStatusIcon(request.status);
            
            return (
              <Card key={request.id} className="card-shadow hover:card-shadow-lg transition-shadow">
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-orange-100 rounded-full">
                        <TypeIcon className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">
                          Room {request.room_number}
                        </CardTitle>
                        <CardDescription className="capitalize">
                          {request.maintenance_type.replace('_', ' ')}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <Badge className={getPriorityColor(request.priority)}>
                        {request.priority}
                      </Badge>
                      <Badge className={getStatusColor(request.status)}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {request.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Description */}
                  <div>
                    <p className="text-sm text-gray-700 line-clamp-3">
                      {request.description}
                    </p>
                  </div>

                  {/* Request Details */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">{request.requested_by}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">
                        {new Date(request.requested_date).toLocaleDateString()}
                      </span>
                    </div>
                    {request.estimated_duration && (
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">{request.estimated_duration}</span>
                      </div>
                    )}
                    {request.cost_estimate && (
                      <div className="flex items-center space-x-2">
                        <DollarSign className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">${request.cost_estimate}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-between pt-4 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedRequest(request);
                        setShowDetailsDialog(true);
                      }}
                      className="flex-1 mr-2"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Details
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedRequest(request);
                        setUpdateData({
                          status: request.status,
                          assigned_to: request.assigned_to || '',
                          completion_notes: request.completion_notes || '',
                          actual_cost: request.actual_cost || ''
                        });
                        setShowUpdateDialog(true);
                      }}
                      className="flex-1 mr-2"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Update
                    </Button>

                    {user?.role === 'admin' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteRequest(request)}
                        className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Maintenance Request Details</DialogTitle>
            <DialogDescription>
              Complete information for maintenance request
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Room</label>
                  <p className="text-sm text-gray-900">Room {selectedRequest.room_number}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Type</label>
                  <p className="text-sm text-gray-900 capitalize">
                    {selectedRequest.maintenance_type.replace('_', ' ')}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Priority</label>
                  <Badge className={getPriorityColor(selectedRequest.priority)}>
                    {selectedRequest.priority}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <Badge className={getStatusColor(selectedRequest.status)}>
                    {selectedRequest.status.replace('_', ' ')}
                  </Badge>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-medium text-gray-700">Description</label>
                <p className="text-sm text-gray-900 mt-1">{selectedRequest.description}</p>
              </div>

              {/* Additional Details */}
              {(selectedRequest.materials_needed || selectedRequest.estimated_duration || selectedRequest.cost_estimate) && (
                <div className="grid grid-cols-1 gap-4">
                  {selectedRequest.materials_needed && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Materials Needed</label>
                      <p className="text-sm text-gray-900 mt-1">{selectedRequest.materials_needed}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    {selectedRequest.estimated_duration && (
                      <div>
                        <label className="text-sm font-medium text-gray-700">Estimated Duration</label>
                        <p className="text-sm text-gray-900">{selectedRequest.estimated_duration}</p>
                      </div>
                    )}
                    {selectedRequest.cost_estimate && (
                      <div>
                        <label className="text-sm font-medium text-gray-700">Cost Estimate</label>
                        <p className="text-sm text-gray-900">${selectedRequest.cost_estimate}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Assignment & Completion Info */}
              {(selectedRequest.assigned_to || selectedRequest.completed_by || selectedRequest.completion_notes) && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Progress Tracking</h4>
                  <div className="space-y-3">
                    {selectedRequest.assigned_to && (
                      <div>
                        <label className="text-sm font-medium text-gray-700">Assigned To</label>
                        <p className="text-sm text-gray-900">{selectedRequest.assigned_to}</p>
                        {selectedRequest.assigned_date && (
                          <p className="text-xs text-gray-500">
                            Assigned on {new Date(selectedRequest.assigned_date).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                    {selectedRequest.completed_by && (
                      <div>
                        <label className="text-sm font-medium text-gray-700">Completed By</label>
                        <p className="text-sm text-gray-900">{selectedRequest.completed_by}</p>
                        {selectedRequest.completed_date && (
                          <p className="text-xs text-gray-500">
                            Completed on {new Date(selectedRequest.completed_date).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                    {selectedRequest.completion_notes && (
                      <div>
                        <label className="text-sm font-medium text-gray-700">Completion Notes</label>
                        <p className="text-sm text-gray-900">{selectedRequest.completion_notes}</p>
                      </div>
                    )}
                    {selectedRequest.actual_cost && (
                      <div>
                        <label className="text-sm font-medium text-gray-700">Actual Cost</label>
                        <p className="text-sm text-gray-900">${selectedRequest.actual_cost}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="border-t pt-4 text-xs text-gray-500">
                <p>Created: {new Date(selectedRequest.created_at).toLocaleString()}</p>
                {selectedRequest.updated_at && selectedRequest.updated_at !== selectedRequest.created_at && (
                  <p>Last Updated: {new Date(selectedRequest.updated_at).toLocaleString()}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Update Dialog */}
      <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Maintenance Request</DialogTitle>
            <DialogDescription>
              Update the status and details of this maintenance request
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Status</label>
              <Select value={updateData.status} onValueChange={(value) => setUpdateData({...updateData, status: value})}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select status..." />
                </SelectTrigger>
                <SelectContent className="z-50">
                  <SelectItem value="requested">🔄 Requested</SelectItem>
                  <SelectItem value="assigned">👤 Assigned</SelectItem>
                  <SelectItem value="in_progress">🔧 In Progress</SelectItem>
                  <SelectItem value="completed">✅ Completed</SelectItem>
                  <SelectItem value="cancelled">❌ Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Assigned To</label>
              <Input
                value={updateData.assigned_to}
                onChange={(e) => setUpdateData({...updateData, assigned_to: e.target.value})}
                placeholder="Enter staff member name..."
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Completion Notes</label>
              <Textarea
                value={updateData.completion_notes}
                onChange={(e) => setUpdateData({...updateData, completion_notes: e.target.value})}
                placeholder="Add notes about work completed..."
                rows={3}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">
                Actual Cost <span className="text-gray-500 font-normal">(Optional - for maintenance staff)</span>
              </label>
              <Input
                type="number"
                step="0.01"
                value={updateData.actual_cost}
                onChange={(e) => setUpdateData({...updateData, actual_cost: e.target.value})}
                placeholder="Leave empty if not applicable"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowUpdateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => handleUpdateRequest(selectedRequest?.id)}
                disabled={updateLoading}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {updateLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Edit className="h-4 w-4 mr-2" />
                    Update Request
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">🗑️ Delete Maintenance Request</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this maintenance request? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {requestToDelete && (
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <div className="space-y-2">
                <p className="text-sm"><strong>Request:</strong> {requestToDelete.description}</p>
                <p className="text-sm"><strong>Room:</strong> {requestToDelete.room_number}</p>
                <p className="text-sm"><strong>Priority:</strong> {requestToDelete.priority}</p>
                <p className="text-sm"><strong>Status:</strong> {requestToDelete.status}</p>
              </div>
            </div>
          )}
          
          <div className="flex justify-end space-x-3 mt-6">
            <Button variant="outline" onClick={cancelDeleteRequest}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteRequest}
              className="bg-red-600 hover:bg-red-700"
            >
              🗑️ Delete Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaintenanceRequests;